import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Users, Crown, UserPlus, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import Avatar from '../components/Avatar';
import toast from 'react-hot-toast';

const JoinPage = () => {
  const { token } = useParams();
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { loadGroup(); }, [token]);

  const loadGroup = async () => {
    try {
      const { data } = await API.get(`/groups/join/${token}`);
      setGroup(data.group);
    } catch (err) {
      setError(err.response?.data?.message || 'رابط الدعوة غير صالح');
    } finally { setLoading(false); }
  };

  const handleJoin = async () => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: { pathname: `/join/${token}` } } });
      return;
    }
    setJoining(true);
    try {
      const { data } = await API.post(`/groups/join/${token}`);
      toast.success(data.message || 'تم الانضمام بنجاح 🎉');
      navigate(`/group/${data.groupId}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في الانضمام');
    } finally { setJoining(false); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)' }}>
      <div className="loading-screen"><div className="spinner" /><p>جاري التحميل...</p></div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)', padding: '20px' }}>
      <div className="card" style={{ maxWidth: '400px', width: '100%', textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>😕</div>
        <h2 style={{ marginBottom: '8px' }}>رابط غير صالح</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>{error}</p>
        <Link to="/" className="btn btn-primary btn-full">العودة للرئيسية</Link>
      </div>
    </div>
  );

  const pct = Math.round((group.currentMembers / group.maxMembers) * 100);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--bg-base)', padding: '20px',
      position: 'relative', overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)',
        width: '500px', height: '400px',
        background: 'radial-gradient(ellipse, rgba(99,102,241,0.12) 0%, transparent 70%)',
        pointerEvents: 'none'
      }} />

      <div style={{ width: '100%', maxWidth: '420px', position: 'relative', zIndex: 1 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Link to="/" style={{
            fontSize: '1.75rem', fontWeight: 900,
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            ظبطت 🎉
          </Link>
        </div>

        <div className="card animate-slideUp" style={{ padding: 0, overflow: 'hidden' }}>
          {/* Group Image */}
          <div style={{
            height: '160px',
            background: group.image ? `url(${group.image}) center/cover` : 'var(--gradient-primary)',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to bottom, transparent 40%, rgba(26,34,53,0.95) 100%)'
            }} />
            {!group.image && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>
                🎉
              </div>
            )}
          </div>

          <div style={{ padding: '24px' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <h1 style={{ fontSize: '1.4rem', fontWeight: 800, marginBottom: '8px' }}>
                {group.name}
              </h1>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                دُعيت للانضمام إلى هذه المجموعة
              </p>
            </div>

            {/* Admin Info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', marginBottom: '16px' }}>
              <Avatar src={group.admin?.avatar} name={group.admin?.name} size="sm" />
              <div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>الأدمن</div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Crown size={13} style={{ color: 'var(--warning)' }} /> {group.admin?.name}
                </div>
              </div>
            </div>

            {/* Members Count */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.82rem' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-secondary)' }}>
                  <Users size={13} /> الأعضاء
                </span>
                <span style={{ color: group.isFull ? 'var(--danger)' : 'var(--text-muted)' }}>
                  {group.currentMembers} / {group.maxMembers}
                </span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{
                  width: `${pct}%`,
                  background: group.isFull ? 'var(--danger)' : 'var(--gradient-primary)'
                }} />
              </div>
              {group.isFull && (
                <p style={{ color: 'var(--danger)', fontSize: '0.78rem', marginTop: '4px', textAlign: 'center' }}>
                  المجموعة ممتلئة 😔
                </p>
              )}
            </div>

            {group.isFull ? (
              <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(239,68,68,0.1)', borderRadius: 'var(--radius-md)', color: 'var(--danger)' }}>
                عذراً، المجموعة وصلت للحد الأقصى من الأعضاء
              </div>
            ) : (
              <div>
                {isLoggedIn ? (
                  <button className="btn btn-primary btn-full btn-lg" onClick={handleJoin} disabled={joining}>
                    {joining ? <div className="spinner spinner-sm" /> : <UserPlus size={18} />}
                    {joining ? 'جاري الانضمام...' : `انضم لـ "${group.name}"`}
                  </button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Link to={`/register?redirect=/join/${token}`} className="btn btn-primary btn-full btn-lg">
                      <UserPlus size={18} /> سجّل وانضم
                    </Link>
                    <Link to="/login" state={{ from: { pathname: `/join/${token}` } }} className="btn btn-secondary btn-full">
                      <LogIn size={16} /> عندي حساب — دخول
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinPage;
